import React, { useState, useEffect } from 'react';
import { Bell, AlertCircle, CheckCircle, Clock, ArrowLeft } from 'lucide-react';
import { Invoice } from '../types';
import { api } from '../services/api';

interface NotificationsProps {
    onClose: () => void;
}

const Notifications: React.FC<NotificationsProps> = ({ onClose }) => {
    const [alerts, setAlerts] = useState<{ id: string, type: 'error' | 'warning' | 'info', title: string, message: string, date: string }[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchAlerts = async () => {
            try {
                // Fetch invoices and contracts in parallel
                const [invoices, contracts] = await Promise.all([
                    api.invoices.getAll(),
                    api.maintenance.contracts.getAll()
                ]);
                
                const today = new Date();
                const newAlerts: any[] = [];

                // 1. Check for overdue invoices
                invoices.forEach(inv => {
                    if (inv.status === 'Pending' || inv.status === 'Late') {
                        const dueDate = new Date(inv.dueDate);
                        if (dueDate < today) {
                            const diffTime = Math.abs(today.getTime() - dueDate.getTime());
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
                            newAlerts.push({
                                id: `inv-${inv.id}`,
                                type: 'error',
                                title: 'فاتورة متأخرة',
                                message: `فاتورة المستأجر ${inv.tenantName} بقيمة ${inv.amount} KWD متأخرة منذ ${diffDays} يوم`,
                                date: inv.dueDate
                            });
                        } else {
                            const diffTime = Math.abs(dueDate.getTime() - today.getTime());
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                            if (diffDays <= 3) {
                                newAlerts.push({
                                    id: `inv-soon-${inv.id}`,
                                    type: 'warning',
                                    title: 'استحقاق قريب',
                                    message: `فاتورة المستأجر ${inv.tenantName} تستحق خلال ${diffDays} يوم`,
                                    date: inv.dueDate
                                });
                            }
                        }
                    }
                });

                // 2. Check for expiring contracts
                contracts.forEach(contract => {
                    if (contract.status === 'Active') {
                        const endDate = new Date(contract.endDate);
                        const diffTime = endDate.getTime() - today.getTime();
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        if (diffDays <= 0) {
                             newAlerts.push({
                                id: `contract-expired-${contract.id}`,
                                type: 'error',
                                title: 'عقد صيانة منتهي',
                                message: `عقد شركة ${contract.companyName} انتهى بتاريخ ${contract.endDate}`,
                                date: contract.endDate
                            });
                        } else if (diffDays <= 30) {
                            newAlerts.push({
                                id: `contract-soon-${contract.id}`,
                                type: 'warning',
                                title: 'انتهاء عقد صيانة',
                                message: `عقد شركة ${contract.companyName} ينتهي خلال ${diffDays} يوم`,
                                date: contract.endDate
                            });
                        }
                    }
                });

                setAlerts(newAlerts);
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAlerts();
    }, []);

    return (
        <div className="fixed inset-y-0 left-0 w-full max-w-sm bg-white shadow-2xl z-[60] border-r border-slate-200 animate-slideRight flex flex-col" dir="rtl">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                <div className="flex items-center gap-3">
                    <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
                        <Bell size={20} className="text-slate-700" />
                    </div>
                    <div>
                        <h2 className="font-black text-slate-800">الإشعارات</h2>
                        <p className="text-[10px] text-slate-400 font-bold">{alerts.length} إشعار جديد</p>
                    </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors active:scale-95">
                    <ArrowLeft size={20} className="text-slate-500" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-40 gap-3 text-slate-400">
                        <div className="w-6 h-6 border-2 border-slate-200 border-t-fly-500 rounded-full animate-spin"></div>
                        <span className="text-xs font-bold">جاري التحميل...</span>
                    </div>
                ) : alerts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-center text-slate-400">
                        <CheckCircle size={48} className="opacity-20 mb-4" />
                        <p className="font-bold text-sm">لا توجد إشعارات جديدة</p>
                        <p className="text-xs opacity-70 mt-1">أنت مطلع على كل شيء!</p>
                    </div>
                ) : (
                    alerts.map(alert => (
                        <div key={alert.id} className={`p-4 rounded-xl border flex gap-3 ${
                            alert.type === 'error' ? 'bg-red-50 border-red-100' : 
                            alert.type === 'warning' ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100'
                        }`}>
                            <div className={`mt-0.5 ${
                                alert.type === 'error' ? 'text-red-600' : 
                                alert.type === 'warning' ? 'text-amber-600' : 'text-blue-600'
                            }`}>
                                {alert.type === 'error' ? <AlertCircle size={18} /> : <Clock size={18} />}
                            </div>
                            <div>
                                <h4 className={`font-bold text-sm mb-1 ${
                                    alert.type === 'error' ? 'text-red-900' : 
                                    alert.type === 'warning' ? 'text-amber-900' : 'text-blue-900'
                                }`}>{alert.title}</h4>
                                <p className={`text-xs leading-relaxed ${
                                    alert.type === 'error' ? 'text-red-800' : 
                                    alert.type === 'warning' ? 'text-amber-800' : 'text-blue-800'
                                }`}>{alert.message}</p>
                                <span className={`text-[10px] font-mono mt-2 block opacity-60 font-bold`}>{alert.date}</span>
                            </div>
                        </div>
                    ))
                )}
            </div>
            
            <div className="p-4 bg-slate-50 border-t border-slate-100 text-center">
                <button onClick={() => setAlerts([])} className="text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors">
                    مسح جميع الإشعارات
                </button>
            </div>
        </div>
    );
};

export default Notifications;
